import {
  Component,
  OnInit,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { FieldWrapper, FormlyFieldConfig } from '@ngx-formly/core';
import { flattenDeep } from 'lodash';
import { MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelContent } from '@angular/material/expansion';

@Component({
    selector: 'app-section-wrapper',
    templateUrl: './section-wrapper.component.html',
    styleUrls: ['./section-wrapper.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelContent]
})
export class FormlyFieldSectionWrapperComponent
  extends FieldWrapper
  implements OnInit
{
  collapsed = false;
  shouldNotHide = false;
  @ViewChild('section', { static: false, read: ViewContainerRef })
  declare fieldComponent: ViewContainerRef;

  ngOnInit(): void {
    this.setDefaultBehaviour();
    this.proxyHideExpression();
    this.createHideExpression(this.getSectionFields());
  }

  /**
   * It sets default collapsed state based on the section config provided by the user
   * This feature is added as a part of EIM-25617
   * @private
   * @memberof FormlyFieldSectionWrapperComponent
   */
  private setDefaultBehaviour(): void {
    if (this.to?.defaultBehaviour === 'expand') {
      this.collapsed = false;
    } else {
      this.collapsed = true;
    }
  }

  /**
   * Proxy the hide expression for each dependent field
   * and only hide the controls if the section is hidden vice versa
   * @private
   * @param {FormlyFieldConfig[]} fields
   * @memberof FormlyFieldSectionWrapperComponent
   */
  private createHideExpression(fields: FormlyFieldConfig[]): void {
    fields.forEach((field) => {
      const oldHideExpression = field.hideExpression;
      if (!oldHideExpression) {
        return;
      }
      const proxyHideExpression = new Proxy(oldHideExpression as any, {
        apply: (target, thisArg, argArray) => {
          if (this.to.hidden) {
            const isReadOnly =
              (argArray[2] as FormlyFieldConfig)?.templateOptions.readonly ??
              false;
            if (!isReadOnly) {
              field?.formControl.reset();
            }
            return true;
          }
          return (target as any).apply(thisArg, argArray);
        },
      });
      field.hideExpression = proxyHideExpression;
    });
  }

  /**
   * This function proxies hide expression method from field
   * We will check if any of the section fields are required then we will not hide the section
   * Else section will hide or display based on the condition
   * @private
   * @memberof FormlyFieldSectionWrapperComponent
   */
  private proxyHideExpression(): void {
    const oldHideExpression = this.field.hideExpression;
    if (!oldHideExpression) {
      return;
    }
    const sectionFields = this.getSectionFields();
    const proxyHideExpression = new Proxy(oldHideExpression as any, {
      apply: (target, thisArg, argArray) => {
        const shouldNotHide = sectionFields.some((f) => f.props?.required);
        if (shouldNotHide) {
          return false;
        }
        return (target as any).apply(thisArg, argArray);
      },
    });
    this.field.hideExpression = proxyHideExpression;
  }

  private getSectionFields() {
    return flattenDeep(this.field.fieldGroup.slice(1).map((f) => f.fieldGroup));
  }
}
